interface ArrayFieldProps {
  label: string;
  value: number[] | undefined;
  onChange: (value: number[]) => void;
  labels?: string[];
  min?: number;
  max?: number;
  step?: number;
  description?: string;
  /** Show "default" badge when all values are zero (meaning inactive/off). */
  showDefaultBadge?: boolean;
}

/**
 * Edits a fixed-length numeric array like [x, y] for translate properties.
 */
export function ArrayField({
  label,
  value,
  onChange,
  labels = ["X", "Y"],
  min,
  max,
  step = 1,
  description,
  showDefaultBadge,
}: ArrayFieldProps) {
  const arr = value ?? labels.map(() => 0);
  const isDefault = showDefaultBadge && (!value || value.every((v) => v === 0));

  const update = (idx: number, val: number) => {
    const next = [...arr];
    next[idx] = val;
    onChange(next);
  };

  return (
    <div className="flex items-center gap-2 py-spacing-field">
      <label className="w-32 shrink-0 text-xs text-text-secondary" title={description}>
        {label}
        {isDefault && (
          <span className="ml-1.5 inline-block rounded-radius-full bg-border-subtle px-1.5 py-px text-[9px] font-medium text-text-muted">
            default
          </span>
        )}
      </label>
      <div className="flex items-center gap-1.5">
        {labels.map((sub, i) => (
          <div key={sub} className="flex items-center gap-0.5">
            <span className="text-[9px] text-text-muted">{sub}</span>
            <input
              type="number"
              value={arr[i] ?? 0}
              onChange={(e) => update(i, Number(e.target.value))}
              min={min}
              max={max}
              step={step}
              className={`h-6 w-14 rounded-radius-sm border border-border-subtle bg-surface-base px-1.5 text-[11px] focus:border-accent focus:outline-none ${
                isDefault ? "text-text-muted" : "text-text-primary"
              }`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
